'use client';

import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { useRouter, usePathname } from '@/i18n/navigation';
import { locales, localeNames, type Locale } from '@/i18n/locales';
import { AnimatePresence, motion } from 'motion/react';
import styles from './LocaleSwitcher.module.css';

const localeFlags: Record<Locale, string> = {
  fr: '🇫🇷',
  en: '🇬🇧',
  es: '🇪🇸',
};

export function LocaleSwitcher({ upward = false }: { upward?: boolean }) {
  const currentLocale = useLocale() as Locale;
  const t = useTranslations('common');
  const router = useRouter();
  const pathname = usePathname();
  const rawParams = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  function handleSelect(locale: Locale) {
    setIsOpen(false);
    if (locale === currentLocale) return;
    // Strip the `locale` segment — next-intl handles it via the second arg
    const { locale: _locale, ...params } = rawParams ?? {};
    router.replace(
      // @ts-expect-error -- pathname + params are valid for current route
      { pathname, params },
      { locale }
    );
  }

  /* Close on outside click */
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  /* Close on Escape */
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <button
        className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`${t('languageSelector')} : ${localeNames[currentLocale]}`}
      >
        <span className={styles.flag} aria-hidden>{localeFlags[currentLocale]}</span>
        <span className={styles.code}>{currentLocale.toUpperCase()}</span>
        <span className={`${styles.chevron} ${isOpen ? styles.chevronUp : ''}`} aria-hidden>
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
            <path
              d="M1.5 3L4.5 6L7.5 3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.ul
            role="listbox"
            className={`${styles.dropdown} ${upward ? styles.dropdownUp : ''}`}
            initial={{ opacity: 0, y: upward ? 8 : -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: upward ? 8 : -8, scale: 0.96 }}
            transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
          >
            {locales.map((locale) => {
              const isCurrent = locale === currentLocale;
              return (
                <li key={locale} role="option" aria-selected={isCurrent}>
                  <button
                    className={`${styles.option} ${isCurrent ? styles.optionCurrent : ''}`}
                    onClick={() => handleSelect(locale)}
                    tabIndex={0}
                  >
                    <span className={styles.optionFlag} aria-hidden>
                      {localeFlags[locale]}
                    </span>
                    <span className={styles.optionLabel}>{localeNames[locale]}</span>
                    {isCurrent && (
                      <span className={styles.checkmark} aria-hidden>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M2.5 6l2.5 2.5 4.5-5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
